"use client"

import type { CareerCenter } from "@/modules/careerCenter/types"
import type { ContentBoost } from "@/modules/dashboard/api/types"
import type { GetLastSavedSearchResponse, JobAdsResult } from "@/modules/jobAds/client/api/types"
import type { AutocompleteSearchProperties } from "@/modules/jobAds/client/components/AutocompleteSearchInput/AutocompleteSearchInput"
import type { TrackingContext } from "@/modules/jobSearch/types/tracking"
import type { CurrentUserSearchConfig } from "@/modules/user/api/types"
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime"
import type Router from "next/dist/shared/lib/router/router"
import type { ParsedUrlQuery } from "querystring"
import type { FunctionComponent, PropsWithChildren } from "react"

import { createContext, useContext, useMemo, useState } from "react"

export type PageContextValue = {
  /**
   * Search results data needed on client side
   */
  facets: JobAdsResult["facets"]
  savedSearch: GetLastSavedSearchResponse
  savedSearchSize: Exclude<JobAdsResult["savedSearchSize"], undefined>
  /**
   * Career Center infos
   */
  careerCenterConf: CareerCenter["careerCenterConf"] | null
  careerCenterId: CareerCenter["id"] | null
  careerCenterLogo: CareerCenter["logo"] | null
  careerCenterMainId: Exclude<CareerCenter["mainCareerCenterId"], undefined> | null
  careerCenterShortName: CareerCenter["shortName"] | null
  /**
   * User infos
   */
  currentUserSearchConfig: CurrentUserSearchConfig | null
  hasUserDailyJobOffersAlert: boolean
  isUserLogged: boolean
  /**
   * Content boost data based on current user
   */
  userContentBoosts?: ContentBoost[]
  /**
   * Feature flags
   */
  isDirectAlgoliaConnectionEnabled: boolean
  isSearchProxyV3ConnectionEnabled: boolean
  isJobAdsFoWoosmapMigrationEnabled: boolean
  isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled: boolean
  isPublicationDateFilterEnabled: boolean
  /**
   * Used to identify the params used in prefiltered searches.
   */
  prefilteredSearchParams: ParsedUrlQuery
  /**
   * The router push method to navigate through pages.
   * It is stored here to be able to use both App & Page router wherever the search is used.
   */
  push: AppRouterInstance["push"] | Router["push"]
  /**
   * Record to store the informations used to track the page events.
   */
  trackingContext: TrackingContext
  /**
   * Callback to use when the keywork field is used
   */
  onSuggestionClicked?: (properties: AutocompleteSearchProperties) => void
  /**
   * Method to update the user data
   */
  updateUserDailyJobOffersAlert?: (property: PageContextValue["hasUserDailyJobOffersAlert"]) => void
  updateUserSavedSearch?: (property: PageContextValue["savedSearch"]) => void
}

const PageContext = createContext<PageContextValue>({
  careerCenterConf: null,
  careerCenterId: null,
  careerCenterLogo: null,
  careerCenterMainId: null,
  careerCenterShortName: null,
  currentUserSearchConfig: null,
  facets: [],
  hasUserDailyJobOffersAlert: false,
  isDirectAlgoliaConnectionEnabled: false,
  isJobAdsFoWoosmapMigrationEnabled: false,
  isPublicationDateFilterEnabled: false,
  isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled: false,
  isSearchProxyV3ConnectionEnabled: false,
  isUserLogged: false,
  onSuggestionClicked: () => {},
  prefilteredSearchParams: {},
  push: () => {},
  savedSearch: null,
  savedSearchSize: 0,
  trackingContext: {} as TrackingContext,
  userContentBoosts: [],
})

export const usePageContext = (): PageContextValue => useContext(PageContext)

type PageContextProviderProps = PropsWithChildren<{
  value: PageContextValue
}>

export const PageContextProvider: FunctionComponent<PageContextProviderProps> = ({ children, value }) => {
  const [hasUserDailyJobOffersAlert, setUserDailyJobOffersAlert] = useState<
    PageContextValue["hasUserDailyJobOffersAlert"]
  >(value.hasUserDailyJobOffersAlert)
  const [savedSearch, setSavedSearch] = useState<PageContextValue["savedSearch"]>(value.savedSearch)

  const contextValue = useMemo(
    () => ({
      ...value,
      hasUserDailyJobOffersAlert,
      savedSearch,
      updateUserDailyJobOffersAlert: setUserDailyJobOffersAlert,
      updateUserSavedSearch: setSavedSearch,
    }),
    [hasUserDailyJobOffersAlert, savedSearch, value]
  )

  return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>
}
